import { Column, Row } from '@/components';
import { useService } from '@/containers/ActionServiceV2/context';
import { MeetTypeE, ServiceModalityE, SupportedCountriesE } from '@/types/cyclone/models';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLaptop, faLocationDot, faPlus, faVideo } from '@fortawesome/free-solid-svg-icons';
import classNames from 'classnames';
import { FunctionComponent, useLayoutEffect } from 'react';
import { ControllerWithInput } from '../ControllerWithInput';
import { useAuth } from '@/contexts';
import { Link, useLocation } from 'react-router-dom';
import { Switch } from '@headlessui/react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/SelectV3';
import { InputAddress } from '@/components/Input/InputAddress';
import TextArea from '@/components/TextArea/TextArea';
import { Controller } from 'react-hook-form';
import { Building2, User, Users, Video } from 'lucide-react';
import { Checkbox } from '@/components/Checkbox';

export const MainTab: FunctionComponent = () => {
  const {
    editFormControl,
    isOnDemand,
    modality,
    setModality,
    meetType,
    setMeetType,
    selectedPlace,
    handleSelectPlace,
    selectedStaffs,
    setSelectedStaffs,
    vendors,
    toggleBufferTime,
    handleToggleBufferTime,
    bufferTime,
    setBufferTime,
    bufferOptions,
    aboutErrors
  } = useService();
  const { session } = useAuth();
  const isVenue = session?.is_venue;
  const location = useLocation();
  const isLocal = modality === ServiceModalityE.LOCAL;

  useLayoutEffect(() => {
    const params = new URLSearchParams(location.search);
    const section = params.get('section');

    if (section === 'staff') {
      setTimeout(() => {
        const staff = document.getElementById('staff');
        if (staff) {
          staff.scrollIntoView({ behavior: 'smooth' });
        }
      }, 300);
    }
  }, [location.search]);

  const toggleSelectAllStaff = () => {
    if (selectedStaffs.length === vendors!.length) {
      setSelectedStaffs([]);
    } else {
      setSelectedStaffs(vendors!.map((staff) => staff.id));
    }
  };

  const areAllStaffsSelected = selectedStaffs.length === vendors?.length;

  return (
    <div className="flex flex-col gap-6 pt-4 px-2 sm:px-0">
      <div className="w-full sm:max-w-[672px] mx-auto sm:border-2 sm:border-gray-200 sm:rounded-xl sm:p-6 sm:bg-white">
        <div className="space-y-8">
          <div className="flex items-center gap-2 my-4">
            <div className="flex w-fit items-center gap-3 rounded-xl border border-blue/20 px-4 py-1 bg-blue/10">
              {isOnDemand ? (
                <User className="h-5 w-5 text-blue flex-shrink-0" />
              ) : (
                <Users className="h-5 w-5 text-blue flex-shrink-0" />
              )}
              <span className="text-base text-blue">{isOnDemand ? 'Personalizado' : 'Grupal'}</span>
            </div>
            <div className="flex w-fit items-center gap-3 rounded-xl border border-blue/20 px-4 py-1 bg-blue/10">
              {isLocal ? (
                <Building2 className="h-5 w-5 text-blue flex-shrink-0" />
              ) : (
                <Video className="h-5 w-5 text-blue flex-shrink-0" />
              )}
              <span className="text-base text-blue">{isLocal ? 'Presencial' : 'Online'}</span>
            </div>
          </div>

          {!isOnDemand && !session?.is_beauty_category && (
            <div className="space-y-4">
              <span className="text-xl font-medium text-gray-900">Participantes</span>
              <div className="w-full sm:w-1/2">
                <ControllerWithInput
                  name="participants"
                  placeholder="Participantes (2 o más)"
                  control={editFormControl}
                  errors={aboutErrors.participants}
                  type="number"
                />
              </div>
            </div>
          )}

          <div className="space-y-4">
            <span className="text-xl font-medium text-gray-900">Nombre del servicio</span>
            <div className="w-full sm:w-[80%]">
              <ControllerWithInput
                name="name"
                placeholder={
                  modality === ServiceModalityE.LOCAL
                    ? session?.is_beauty_category
                      ? 'Ej: Esmaltado semipermanente'
                      : 'Ej: Consulta presencial'
                    : 'Ej: Consulta online'
                }
                control={editFormControl}
                errors={aboutErrors.name}
                maxLength={60}
              />
            </div>
          </div>

          <div className="space-y-4">
            <span className="text-xl font-medium text-gray-900">Duración de cada encuentro</span>
            <div className="w-full sm:w-1/2">
              <ControllerWithInput
                name="duration"
                placeholder="Duración"
                postfix="mins"
                control={editFormControl}
                errors={aboutErrors.duration}
                type="number"
              />
            </div>
          </div>

          <div className="space-y-4">
            <div className="space-y-1">
              <div className="flex items-center gap-4">
                <span className="text-xl font-medium text-gray-900">Bloquear tiempo adicional</span>
                <Switch
                  checked={toggleBufferTime}
                  onChange={(checked) => handleToggleBufferTime(checked)}
                  className={`${
                    toggleBufferTime ? 'bg-blue/20' : 'bg-gray-300'
                  } relative inline-flex h-6 w-11 items-center rounded-full transition-colors`}
                >
                  <span
                    className={`${
                      toggleBufferTime ? 'translate-x-6 bg-blue' : 'translate-x-1 bg-white'
                    } inline-block h-4 w-4 transform rounded-full transition-transform`}
                  />
                </Switch>
              </div>
              <p className="text-sm text-[#626262] sm:w-[80%]">
                Bloquear margen de tiempo luego de cada turno, durante el cual no se permiten reservas.
              </p>
            </div>
            {toggleBufferTime && (
              <div className="flex items-center gap-4 w-full sm:w-[80%]">
                <div className="w-full sm:w-1/2">
                  <Select onValueChange={setBufferTime} defaultValue={bufferTime}>
                    <SelectTrigger className="w-full !text-base !border-[#0000005c]">
                      <SelectValue placeholder="Seleccioná una opción">
                        {bufferOptions.find((option) => option.value === bufferTime)?.label}
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent className="!max-h-[200px]">
                      {bufferOptions.map((option, i) => (
                        <SelectItem
                          className={classNames({
                            'bg-blue-500 text-white': option.value === bufferTime,
                            'hover:bg-gray-100': option.value !== bufferTime
                          })}
                          key={i}
                          value={option.value as string}
                        >
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <span className="text-sm text-[#626262] whitespace-nowrap">luego de cada turno</span>
              </div>
            )}
          </div>

          <div className="space-y-4">
            <span className="text-xl font-medium text-gray-900">
              {modality === ServiceModalityE.LOCAL ? 'Dirección' : 'Link de la reunión'}
            </span>
            <Column gap={20}>
              <Row
                align="center"
                justify="space-between"
                gap={8}
                className="w-full md:w-[80%] p-2 text-base font-medium bg-[#F4F4F4] rounded"
              >
                <Row
                  align="center"
                  justify="center"
                  gap={8}
                  onClick={() => setModality(ServiceModalityE.LOCAL)}
                  className={classNames({
                    'p-2 w-1/2 cursor-pointer': true,
                    'bg-white rounded shadow-[0px_2px_2px_0px_#00000040] transition-all duration-300':
                      modality === ServiceModalityE.LOCAL
                  })}
                >
                  <FontAwesomeIcon icon={faLocationDot} className="text-[#868686]" />
                  <span className="text-base text-[#868686] text-center">Presencial</span>
                </Row>
                <Row
                  align="center"
                  justify="center"
                  gap={8}
                  onClick={() => {
                    setModality(ServiceModalityE.VIRTUAL);
                    setMeetType(MeetTypeE.ZOOM);
                  }}
                  className={classNames({
                    'p-2 w-1/2 cursor-pointer': true,
                    'bg-white rounded shadow-[0px_2px_2px_0px_#00000040] transition-all duration-300':
                      modality === ServiceModalityE.VIRTUAL && meetType === MeetTypeE.ZOOM
                  })}
                >
                  <FontAwesomeIcon icon={faVideo} className="text-[#868686]" />
                  <span className="text-base text-[#868686] text-center">Zoom</span>
                </Row>
                <Row
                  align="center"
                  justify="center"
                  gap={8}
                  onClick={() => {
                    setModality(ServiceModalityE.VIRTUAL);
                    setMeetType(MeetTypeE.GOOGLE_MEET);
                  }}
                  className={classNames({
                    'p-2 w-1/2 cursor-pointer': true,
                    'bg-white rounded shadow-[0px_2px_2px_0px_#00000040] transition-all duration-300':
                      modality === ServiceModalityE.VIRTUAL && meetType === MeetTypeE.GOOGLE_MEET
                  })}
                >
                  <FontAwesomeIcon icon={faLaptop} className="text-[#868686]" />
                  <span className="text-base text-[#868686] text-center">Meet</span>
                </Row>
              </Row>
              {modality === ServiceModalityE.LOCAL ? (
                <div className="space-y-4">
                  <div className="w-full sm:w-[80%]">
                    <InputAddress
                      country={session?.vendor?.country || SupportedCountriesE.ARGENTINA}
                      state={session?.vendor?.state}
                      selectedPlace={selectedPlace}
                      onSelect={handleSelectPlace}
                    />
                  </div>
                  <div className="w-full sm:w-[80%]">
                    <Controller
                      control={editFormControl}
                      name="details"
                      render={({ field: { onChange, value } }) => (
                        <TextArea
                          placeholder="Piso, departamento, otras instrucciones"
                          value={value || ''}
                          onChange={onChange}
                          autoExpand={false}
                          maxLength={70}
                          showCount
                        />
                      )}
                    />
                  </div>
                </div>
              ) : (
                <div className="w-full sm:w-[80%]">
                  <ControllerWithInput
                    name="meetLink"
                    control={editFormControl}
                    errors={aboutErrors.meetLink}
                    placeholder="Pegá el link de la reunión"
                  />
                </div>
              )}
            </Column>
          </div>

          {isVenue && isOnDemand && (
            <div id="staff" className="space-y-4">
              <div className="space-y-1">
                <span className="text-xl font-medium text-gray-900">Personal</span>
                <p className="text-sm text-[#626262] sm:w-[80%]">
                  Selecciona los integrantes de tu equipo que prestan este servicio.
                </p>
              </div>
              <div className="w-full">
                {!vendors ? (
                  <Link to={'/equipo/agregar'} target="_blank">
                    <div className="flex items-center justify-center gap-2 w-fit p-3 rounded-xl border-2 border-gray-200 hover:border-blue/20 transition-all duration-200">
                      <FontAwesomeIcon icon={faPlus} className="text-blue h-5 w-5" />
                    </div>
                  </Link>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div
                      onClick={toggleSelectAllStaff}
                      className="flex items-center gap-4 px-4 py-2 border-2 border-gray-200 rounded-xl cursor-pointer hover:border-blue/20 transition-all duration-200"
                    >
                      <Checkbox enable={areAllStaffsSelected} />
                      <p className="text-blue-500">Seleccionar todo el personal</p>
                    </div>
                    {vendors
                      .sort((a, b) =>
                        `${a.user.first_name} ${a.user.last_name}`.localeCompare(
                          `${b.user.first_name} ${b.user.last_name}`
                        )
                      )
                      .map((staff) => {
                        const isSelected = selectedStaffs.includes(staff.id);
                        const handleSelectStaff = () => {
                          if (isSelected) {
                            setSelectedStaffs(selectedStaffs.filter((id) => id !== staff.id));
                          } else {
                            setSelectedStaffs([...selectedStaffs, staff.id]);
                          }
                        };
                        return (
                          <div
                            key={staff.id}
                            onClick={handleSelectStaff}
                            className={`flex items-center gap-4 px-4 py-2 rounded-xl border-2 transition-all duration-200 cursor-pointer ${
                              isSelected ? 'border-blue bg-blue/10' : 'border-gray-200 hover:border-blue/20'
                            }`}
                          >
                            <Checkbox enable={isSelected} />
                            <span className="text-gray-900">
                              {staff.user.first_name} {staff.user.last_name}
                            </span>
                          </div>
                        );
                      })}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
